import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Pressable,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  SafeAreaView,
  Image,
  Dimensions,
  TextInput as RNTextInput,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Text from '../../components/common/Text';
import { palette } from '../../utils/palette';
import { RootStackParamList } from '../../types/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth, authKeys } from '../../hooks/useAuth';
import { reset as resetNavigation } from '../../navigation/NavigationService';
import { getPostAuthRouteFromAuthState } from '../../utils/authNavigation';
import TextField from '../../components/common/TextField';
import Button from '../../components/common/Button';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import ResponsiveContainer from '../../components/shared/ResponsiveContainer';
import EyeIcon from '../../components/common/EyeIcon';
import { useTheme } from '../../hooks/useTheme';
import { trackButtonClick } from '../../utils/analytics';
import { logger } from '../../utils/logger';
import { formatLoginError } from '../../utils/authErrors';

// Form validation schema
const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginScreen = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const queryClient = useQueryClient();
  const { signIn, isSigningIn } = useAuth();
  const { isMobile, screenWidth } = useResponsiveLayout();
  const { colors } = useTheme();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const emailInputRef = useRef<RNTextInput>(null);

  useEffect(() => {
    const focusEmail = () => {
      if (emailInputRef.current) {
        emailInputRef.current.focus();
      } else {
        // Retry if ref is not ready yet
        setTimeout(focusEmail, 50);
      }
    };

    const timer = setTimeout(focusEmail, 200);
    return () => clearTimeout(timer);
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoginError(null);
      await signIn(data.email, data.password);
      const user = queryClient.getQueryData(authKeys.user()) as
        | { onboarding_completed?: boolean; email_verified?: boolean }
        | null
        | undefined;
      const pendingEmailVerify =
        queryClient.getQueryData(authKeys.pendingEmailVerify()) === true;
      resetNavigation(0, [
        getPostAuthRouteFromAuthState({
          user: user ?? null,
          pendingEmailVerify,
        }),
      ]);
    } catch (error: unknown) {
      logger.error('[LoginScreen] Sign-in failed:', error);
      setLoginError(formatLoginError(error));
    }
  };

  const handleLogoPress = () => {
    navigation.navigate('Landing');
  };

  // Animation useEffect - commented out temporarily
  // useEffect(() => {
  //   const createBubbleAnimation = (animation: Animated.Value, delay: number, duration: number) => {
  //     return Animated.loop(
  //       Animated.sequence([
  //         Animated.delay(delay),
  //         Animated.timing(animation, {
  //           toValue: 1,
  //           duration: duration,
  //           useNativeDriver: Platform.OS !== 'web',
  //         }),
  //         Animated.timing(animation, {
  //           toValue: 0,
  //           duration: duration,
  //           useNativeDriver: Platform.OS !== 'web',
  //         }),
  //       ])
  //     );
  //   };

  //   createBubbleAnimation(bubbleAnimation1, 0, 3000).start();
  //   createBubbleAnimation(bubbleAnimation2, 500, 4000).start();
  //   createBubbleAnimation(bubbleAnimation3, 1000, 3500).start();
  //   createBubbleAnimation(bubbleAnimation4, 1500, 4500).start();
  //   createBubbleAnimation(bubbleAnimation5, 2000, 5000).start();
  //   createBubbleAnimation(bubbleAnimation6, 2500, 3200).start();
  //   createBubbleAnimation(bubbleAnimation7, 3000, 3800).start();
  //   createBubbleAnimation(bubbleAnimation8, 3500, 4200).start();
  //   createBubbleAnimation(bubbleAnimation9, 4000, 3600).start();
  //   createBubbleAnimation(bubbleAnimation10, 4500, 4800).start();
  //   createBubbleAnimation(bubbleAnimation11, 5000, 3400).start();
  //   createBubbleAnimation(bubbleAnimation12, 5500, 4600).start();
  //   createBubbleAnimation(bubbleAnimation13, 6000, 3900).start();
  //   createBubbleAnimation(bubbleAnimation14, 6500, 4300).start();
  //   createBubbleAnimation(bubbleAnimation15, 7000, 3700).start();
  //   createBubbleAnimation(bubbleAnimation16, 7500, 4200).start();
  //   createBubbleAnimation(bubbleAnimation17, 8000, 3600).start();
  //   createBubbleAnimation(bubbleAnimation18, 8500, 4400).start();
  //   createBubbleAnimation(bubbleAnimation19, 9000, 3800).start();
  //   createBubbleAnimation(bubbleAnimation20, 9500, 4000).start();
  // }, [bubbleAnimation1, bubbleAnimation2, bubbleAnimation3, bubbleAnimation4, bubbleAnimation5, bubbleAnimation6, bubbleAnimation7, bubbleAnimation8, bubbleAnimation9, bubbleAnimation10, bubbleAnimation11, bubbleAnimation12, bubbleAnimation13, bubbleAnimation14, bubbleAnimation15, bubbleAnimation16, bubbleAnimation17, bubbleAnimation18, bubbleAnimation19, bubbleAnimation20]);

  const isTablet = screenWidth >= 768 && screenWidth < 1024;
  const logoSize = isMobile ? 40 : isTablet ? 50 : 60;
  const formPadding = isMobile ? 24 : isTablet ? 40 : 50;

  return (
    <ResponsiveContainer
      style={[styles.container, { backgroundColor: colors.authBackground }]}
    >
      {/* Concave Circle Background */}
      {/* <View style={styles.concaveCircleBackground}>
        <View style={styles.concaveCircle} />
      </View> */}

      {/* Floating Bubbles Background - commented out temporarily */}
      {/* <View style={styles.bubblesContainer}>
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble1,
            {
              transform: [
                {
                  translateY: bubbleAnimation1.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -50],
                  }),
                },
                {
                  scale: bubbleAnimation1.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.8, 1.2, 0.8],
                  }),
                },
              ],
              opacity: bubbleAnimation1.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.4, 0.8, 0.4],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble2,
            {
              transform: [
                {
                  translateY: bubbleAnimation2.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -80],
                  }),
                },
                {
                  scale: bubbleAnimation2.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.6, 1.0, 0.6],
                  }),
                },
              ],
              opacity: bubbleAnimation2.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.3, 0.7, 0.3],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble3,
            {
              transform: [
                {
                  translateY: bubbleAnimation3.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -60],
                  }),
                },
                {
                  scale: bubbleAnimation3.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.7, 1.1, 0.7],
                  }),
                },
              ],
              opacity: bubbleAnimation3.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.25, 0.6, 0.25],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble4,
            {
              transform: [
                {
                  translateY: bubbleAnimation4.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -70],
                  }),
                },
                {
                  scale: bubbleAnimation4.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.5, 0.9, 0.5],
                  }),
                },
              ],
              opacity: bubbleAnimation4.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble5,
            {
              transform: [
                {
                  translateY: bubbleAnimation5.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -40],
                  }),
                },
                {
                  scale: bubbleAnimation5.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.9, 1.3, 0.9],
                  }),
                },
              ],
              opacity: bubbleAnimation5.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.3, 0.6, 0.3],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble6,
            {
              transform: [
                {
                  translateY: bubbleAnimation6.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -90],
                  }),
                },
                {
                  scale: bubbleAnimation6.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.8, 1.4, 0.8],
                  }),
                },
              ],
              opacity: bubbleAnimation6.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble7,
            {
              transform: [
                {
                  translateY: bubbleAnimation7.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -75],
                  }),
                },
                {
                  scale: bubbleAnimation7.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.6, 1.1, 0.6],
                  }),
                },
              ],
              opacity: bubbleAnimation7.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.25, 0.5, 0.25],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble8,
            {
              transform: [
                {
                  translateY: bubbleAnimation8.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -65],
                  }),
                },
                {
                  scale: bubbleAnimation8.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.7, 1.3, 0.7],
                  }),
                },
              ],
              opacity: bubbleAnimation8.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble9,
            {
              transform: [
                {
                  translateY: bubbleAnimation9.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -85],
                  }),
                },
                {
                  scale: bubbleAnimation9.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.5, 1.0, 0.5],
                  }),
                },
              ],
              opacity: bubbleAnimation9.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble10,
            {
              transform: [
                {
                  translateY: bubbleAnimation10.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -95],
                  }),
                },
                {
                  scale: bubbleAnimation10.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.9, 1.5, 0.9],
                  }),
                },
              ],
              opacity: bubbleAnimation10.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.15, 0.4, 0.15],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble11,
            {
              transform: [
                {
                  translateY: bubbleAnimation11.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -55],
                  }),
                },
                {
                  scale: bubbleAnimation11.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.8, 1.2, 0.8],
                  }),
                },
              ],
              opacity: bubbleAnimation11.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble12,
            {
              transform: [
                {
                  translateY: bubbleAnimation12.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -70],
                  }),
                },
                {
                  scale: bubbleAnimation12.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.6, 1.1, 0.6],
                  }),
                },
              ],
              opacity: bubbleAnimation12.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble13,
            {
              transform: [
                {
                  translateY: bubbleAnimation13.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -80],
                  }),
                },
                {
                  scale: bubbleAnimation13.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.7, 1.3, 0.7],
                  }),
                },
              ],
              opacity: bubbleAnimation13.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble14,
            {
              transform: [
                {
                  translateY: bubbleAnimation14.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -60],
                  }),
                },
                {
                  scale: bubbleAnimation14.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.5, 0.9, 0.5],
                  }),
                },
              ],
              opacity: bubbleAnimation14.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble15,
            {
              transform: [
                {
                  translateY: bubbleAnimation15.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -100],
                  }),
                },
                {
                  scale: bubbleAnimation15.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.8, 1.4, 0.8],
                  }),
                },
              ],
              opacity: bubbleAnimation15.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.15, 0.4, 0.15],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble16,
            {
              transform: [
                {
                  translateY: bubbleAnimation16.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -45],
                  }),
                },
                {
                  scale: bubbleAnimation16.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.7, 1.1, 0.7],
                  }),
                },
              ],
              opacity: bubbleAnimation16.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble17,
            {
              transform: [
                {
                  translateY: bubbleAnimation17.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -65],
                  }),
                },
                {
                  scale: bubbleAnimation17.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.6, 1.0, 0.6],
                  }),
                },
              ],
              opacity: bubbleAnimation17.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble18,
            {
              transform: [
                {
                  translateY: bubbleAnimation18.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -55],
                  }),
                },
                {
                  scale: bubbleAnimation18.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.8, 1.2, 0.8],
                  }),
                },
              ],
              opacity: bubbleAnimation18.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble19,
            {
              transform: [
                {
                  translateY: bubbleAnimation19.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -75],
                  }),
                },
                {
                  scale: bubbleAnimation19.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.5, 0.9, 0.5],
                  }),
                },
              ],
              opacity: bubbleAnimation19.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.18, 0.45, 0.18],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.bubble,
            styles.bubble20,
            {
              transform: [
                {
                  translateY: bubbleAnimation20.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -85],
                  }),
                },
                {
                  scale: bubbleAnimation20.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.7, 1.3, 0.7],
                  }),
                },
              ],
              opacity: bubbleAnimation20.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0.2, 0.5, 0.2],
              }),
            },
          ]}
        />
      </View> */}
      {isMobile ? (
        <SafeAreaView style={styles.mobileSafeArea}>
          <KeyboardAvoidingView
            style={styles.mobileKeyboardView}
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            keyboardVerticalOffset={0}
          >
            <ScrollView
              contentContainerStyle={styles.mobileScrollContent}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              bounces={true}
            >
              <View style={styles.mobileContentWrapper}>
                <View
                  style={[
                    styles.formContainer,
                    {
                      backgroundColor: colors.card,
                      shadowColor: colors.border,
                      borderColor: colors.borderLight,
                      paddingHorizontal: formPadding,
                    },
                  ]}
                >
                  {/* Form Header */}
                  <View style={styles.formHeader}>
                    <Pressable
                      onPress={handleLogoPress}
                      style={({ focused }) => [
                        styles.logoContainer,
                        Platform.OS === 'web' &&
                          focused && {
                            outlineWidth: 2,
                            outlineStyle: 'solid',
                            outlineColor: colors.theme_color || colors.primary,
                            outlineOffset: 2,
                          },
                      ]}
                    >
                      <Image
                        source={require('../../../assets/logo-square.png')}
                        style={{ width: logoSize, height: logoSize }}
                        resizeMode="contain"
                      />
                    </Pressable>
                    <Text
                      size="md"
                      weight="bold"
                      style={[styles.formTitle, { color: colors.primary }]}
                    >
                      Sign In
                    </Text>
                    <Text
                      size="sm"
                      style={[
                        styles.formSubtitle,
                        {
                          color: colors.secondary,
                          fontSize: isMobile ? 14 : 17,
                        },
                      ]}
                    >
                      Sign in to your ColourSense account
                    </Text>
                  </View>

                  <View style={styles.form}>
                    <Controller
                      control={control}
                      name="email"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <TextField
                          ref={emailInputRef}
                          label="Email Address"
                          placeholder="Enter your email address"
                          keyboardType="email-address"
                          autoCapitalize="none"
                          value={value}
                          onChangeText={onChange}
                          onBlur={onBlur}
                          error={errors.email?.message}
                        />
                      )}
                    />

                    <Controller
                      control={control}
                      name="password"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <TextField
                          label="Password"
                          placeholder="Enter your password"
                          secureTextEntry={!showPassword}
                          value={value}
                          onChangeText={onChange}
                          onBlur={onBlur}
                          error={errors.password?.message}
                          rightIcon={<EyeIcon isVisible={showPassword} />}
                          onRightIconPress={() =>
                            setShowPassword(!showPassword)
                          }
                        />
                      )}
                    />
                  </View>

                  {/* Forgot Password Link */}
                  <Pressable
                    style={({ focused }) => [
                      styles.forgotPasswordContainer,
                      Platform.OS === 'web' &&
                        focused && {
                          outlineWidth: 2,
                          outlineStyle: 'solid',
                          outlineColor: colors.theme_color || colors.primary,
                          outlineOffset: 2,
                          borderRadius: 6,
                        },
                    ]}
                    onPress={() => navigation.navigate('ForgotPassword')}
                  >
                    <Text
                      size="sm"
                      weight="medium"
                      style={[
                        styles.forgotPasswordText,
                        { color: colors.primary },
                      ]}
                    >
                      Forgot your password?
                    </Text>
                  </Pressable>

                  {/* Error Display */}
                  {loginError && (
                    <View
                      style={[
                        styles.errorContainer,
                        { backgroundColor: colors.errorBackground },
                      ]}
                    >
                      <Text
                        size="sm"
                        weight="medium"
                        style={[styles.errorText, { color: colors.errorText }]}
                      >
                        {loginError}
                      </Text>
                    </View>
                  )}

                  {/* Sign In Button */}
                  <Button
                    title="Sign In"
                    onPress={handleSubmit(onSubmit)}
                    loading={isSigningIn}
                    disabled={isSigningIn}
                    style={styles.signInButton}
                    trackClick={true}
                    trackingLocation="login-screen"
                    trackingData={{
                      button_name: 'Sign In',
                      button_type: 'sign_in',
                      screen: 'login',
                    }}
                  />

                  {/* Footer */}
                  <View style={styles.footer}>
                    <Text
                      size="md"
                      style={[
                        styles.footerText,
                        {
                          color: colors.secondary,
                          fontSize: isMobile ? 14 : 17,
                        },
                      ]}
                    >
                      Don&apos;t have an account?{' '}
                    </Text>
                    <Pressable
                      onPress={() => {
                        trackButtonClick('Create Account', 'login-screen', {
                          button_type: 'create_account_link',
                          screen: 'login',
                        });
                        navigation.navigate('Register');
                      }}
                      style={({ focused }) =>
                        Platform.OS === 'web' && focused
                          ? {
                              outlineWidth: 2,
                              outlineStyle: 'solid',
                              outlineColor:
                                colors.theme_color || colors.primary,
                              outlineOffset: 2,
                              borderRadius: 6,
                            }
                          : {}
                      }
                    >
                      <Text
                        size="md"
                        weight="semibold"
                        style={[
                          styles.footerLink,
                          {
                            color: colors.primary,
                            fontSize: isMobile ? 14 : 17,
                          },
                        ]}
                      >
                        Create Account
                      </Text>
                    </Pressable>
                  </View>

                  {/* Legal Links */}
                  <View style={styles.legalLinks}>
                    <Pressable
                      onPress={() => navigation.navigate('TermsOfService')}
                      hitSlop={{ top: 10, bottom: 10, left: 5, right: 5 }}
                      style={({ focused }) =>
                        Platform.OS === 'web' && focused
                          ? {
                              outlineWidth: 2,
                              outlineStyle: 'solid',
                              outlineColor:
                                colors.theme_color || colors.primary,
                              outlineOffset: 2,
                              borderRadius: 4,
                            }
                          : {}
                      }
                    >
                      <Text
                        size="xs"
                        style={[styles.legalText, { color: colors.primary }]}
                      >
                        Terms of Use
                      </Text>
                    </Pressable>
                    <View style={styles.separatorContainer}>
                      <Text
                        size="xs"
                        style={[styles.legalText, { color: colors.secondary }]}
                      >
                        {' | '}
                      </Text>
                    </View>
                    <Pressable
                      onPress={() => navigation.navigate('PrivacyPolicy')}
                      hitSlop={{ top: 10, bottom: 10, left: 5, right: 5 }}
                      style={({ focused }) =>
                        Platform.OS === 'web' && focused
                          ? {
                              outlineWidth: 2,
                              outlineStyle: 'solid',
                              outlineColor:
                                colors.theme_color || colors.primary,
                              outlineOffset: 2,
                              borderRadius: 4,
                            }
                          : {}
                      }
                    >
                      <Text
                        size="xs"
                        style={[styles.legalText, { color: colors.primary }]}
                      >
                        Privacy Policy
                      </Text>
                    </Pressable>
                  </View>
                </View>
              </View>
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      ) : (
        <KeyboardAvoidingView
          style={[styles.keyboardAvoidingView, { zIndex: 10 }]}
          behavior={undefined}
        >
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContentContainer}
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
            bounces={false}
            scrollEnabled={false}
          >
            <View style={[styles.contentContainer, styles.webContentContainer]}>
              <View
                style={[
                  styles.formContainer,
                  {
                    backgroundColor: colors.card,
                    shadowColor: colors.border,
                    borderColor: colors.borderLight,
                    paddingHorizontal: formPadding,
                  },
                ]}
              >
                {/* Form Header */}
                <View style={styles.formHeader}>
                  <Pressable
                    onPress={handleLogoPress}
                    style={({ focused }) => [
                      styles.logoContainer,
                      Platform.OS === 'web' &&
                        focused && {
                          outlineWidth: 2,
                          outlineStyle: 'solid',
                          outlineColor: colors.theme_color || colors.primary,
                          outlineOffset: 2,
                        },
                    ]}
                  >
                    <Image
                      source={require('../../../assets/logo-square.png')}
                      style={{ width: logoSize, height: logoSize }}
                      resizeMode="contain"
                    />
                  </Pressable>
                  <Text
                    size="md"
                    weight="bold"
                    style={[styles.formTitle, { color: colors.primary }]}
                  >
                    Sign In
                  </Text>
                  <Text
                    size="sm"
                    style={[
                      styles.formSubtitle,
                      { color: colors.secondary, fontSize: isMobile ? 14 : 17 },
                    ]}
                  >
                    Sign in to your ColourSense account
                  </Text>
                </View>

                <View style={styles.form}>
                  <Controller
                    control={control}
                    name="email"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextField
                        ref={emailInputRef}
                        label="Email Address"
                        placeholder="Enter your email address"
                        keyboardType="email-address"
                        autoCapitalize="none"
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        error={errors.email?.message}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="password"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextField
                        label="Password"
                        placeholder="Enter your password"
                        secureTextEntry={!showPassword}
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        error={errors.password?.message}
                        rightIcon={<EyeIcon isVisible={showPassword} />}
                        onRightIconPress={() => setShowPassword(!showPassword)}
                      />
                    )}
                  />
                </View>

                <Pressable
                  style={({ focused }) => [
                    styles.forgotPasswordContainer,
                    Platform.OS === 'web' &&
                      focused && {
                        outlineWidth: 2,
                        outlineStyle: 'solid',
                        outlineColor: colors.theme_color || colors.primary,
                        outlineOffset: 2,
                        borderRadius: 6,
                      },
                  ]}
                  onPress={() => navigation.navigate('ForgotPassword')}
                >
                  <Text
                    size="sm"
                    weight="medium"
                    style={[
                      styles.forgotPasswordText,
                      { color: colors.primary },
                    ]}
                  >
                    Forgot your password?
                  </Text>
                </Pressable>

                {/* Error Display */}
                {loginError && (
                  <View
                    style={[
                      styles.errorContainer,
                      { backgroundColor: colors.errorBackground },
                    ]}
                  >
                    <Text
                      size="sm"
                      weight="medium"
                      style={[styles.errorText, { color: colors.errorText }]}
                    >
                      {loginError}
                    </Text>
                  </View>
                )}

                {/* Sign In Button */}
                <Button
                  title="Sign In"
                  onPress={handleSubmit(onSubmit)}
                  loading={isSigningIn}
                  disabled={isSigningIn}
                  style={styles.signInButton}
                  trackClick={true}
                  trackingLocation="login-screen"
                  trackingData={{
                    button_name: 'Sign In',
                    button_type: 'sign_in',
                    screen: 'login',
                  }}
                />

                {/* Footer */}
                <View style={styles.footer}>
                  <Text
                    size="md"
                    style={[
                      styles.footerText,
                      { color: colors.secondary, fontSize: isMobile ? 14 : 17 },
                    ]}
                  >
                    Don&apos;t have an account?{' '}
                  </Text>
                  <Pressable
                    onPress={() => {
                      trackButtonClick('Create Account', 'login-screen', {
                        button_type: 'create_account_link',
                        screen: 'login',
                      });
                      navigation.navigate('Register');
                    }}
                    style={({ focused }) =>
                      Platform.OS === 'web' && focused
                        ? {
                            outlineWidth: 2,
                            outlineStyle: 'solid',
                            outlineColor: colors.theme_color || colors.primary,
                            outlineOffset: 2,
                            borderRadius: 6,
                          }
                        : {}
                    }
                  >
                    <Text
                      size="md"
                      weight="semibold"
                      style={[
                        styles.footerLink,
                        { color: colors.primary, fontSize: isMobile ? 14 : 17 },
                      ]}
                    >
                      Create Account
                    </Text>
                  </Pressable>
                </View>

                {/* Legal Links */}
                <View style={styles.legalLinks}>
                  <Pressable
                    onPress={() => navigation.navigate('TermsOfService')}
                    hitSlop={{ top: 10, bottom: 10, left: 5, right: 5 }}
                    style={({ focused }) =>
                      Platform.OS === 'web' && focused
                        ? {
                            outlineWidth: 2,
                            outlineStyle: 'solid',
                            outlineColor: colors.theme_color || colors.primary,
                            outlineOffset: 2,
                            borderRadius: 4,
                          }
                        : {}
                    }
                  >
                    <Text
                      size="xs"
                      style={[styles.legalText, { color: colors.primary }]}
                    >
                      Terms of Use
                    </Text>
                  </Pressable>
                  <View style={styles.separatorContainer}>
                    <Text
                      size="xs"
                      style={[styles.legalText, { color: colors.secondary }]}
                    >
                      {' | '}
                    </Text>
                  </View>
                  <Pressable
                    onPress={() => navigation.navigate('PrivacyPolicy')}
                    hitSlop={{ top: 10, bottom: 10, left: 5, right: 5 }}
                    style={({ focused }) =>
                      Platform.OS === 'web' && focused
                        ? {
                            outlineWidth: 2,
                            outlineStyle: 'solid',
                            outlineColor: colors.theme_color || colors.primary,
                            outlineOffset: 2,
                            borderRadius: 4,
                          }
                        : {}
                    }
                  >
                    <Text
                      size="xs"
                      style={[styles.legalText, { color: colors.primary }]}
                    >
                      Privacy Policy
                    </Text>
                  </Pressable>
                </View>
              </View>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      )}
    </ResponsiveContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContentContainer: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    ...(Platform.OS === 'web'
      ? { minHeight: '100%' }
      : {
          minHeight: Dimensions.get('window').height,
          paddingTop: 0,
          paddingBottom: 0,
        }),
  },
  contentContainer: {
    flex: Platform.OS === 'web' ? 0 : 1,
    padding: Platform.OS === 'web' ? 20 : 16,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: Platform.OS === 'web' ? undefined : '100%',
    alignSelf: 'center',
    minHeight:
      Platform.OS === 'web' ? undefined : Dimensions.get('window').height,
  },
  formContainer: {
    borderRadius: Platform.OS === 'web' ? 20 : 24,
    paddingHorizontal: Platform.OS === 'web' ? 50 : 24,
    paddingVertical: Platform.OS === 'web' ? 35 : 32,
    minHeight: Platform.OS === 'web' ? 320 : undefined,
    shadowOffset: {
      width: 0,
      height: Platform.OS === 'web' ? 12 : 8,
    },
    shadowOpacity: 0.3,
    shadowRadius: Platform.OS === 'web' ? 25 : 16,
    elevation: Platform.OS === 'web' ? 12 : 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    width: '100%',
    maxWidth: Platform.OS === 'web' ? undefined : '100%',
    alignSelf: 'center',
  },
  title: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
    letterSpacing: -0.5,
  },
  subtitle: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    textAlign: 'center',
    lineHeight: 22,
    opacity: 0.8,
  },
  formHeader: {
    marginBottom: Platform.OS === 'web' ? 12 : 20,
    alignItems: 'center',
    width: '100%',
  },
  logoContainer: {
    marginBottom: Platform.OS === 'web' ? 8 : 16,
    padding: Platform.OS === 'web' ? 8 : 10,
    borderRadius: Platform.OS === 'web' ? 12 : 14,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignSelf: 'center',
    ...(Platform.OS === 'web' && {
      outlineWidth: 0,
      outlineColor: 'transparent',
      outlineStyle: 'solid',
    }),
  },
  formTitle: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    fontSize: Platform.OS === 'web' ? 16 : 24,
    fontWeight: '800',
    marginBottom: Platform.OS === 'web' ? 4 : 8,
    letterSpacing: Platform.OS === 'web' ? -0.8 : -0.5,
    textShadowColor: 'rgba(0, 0, 0, 0.1)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
    textAlign: 'center',
    width: '100%',
  },
  formSubtitle: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    opacity: 0.8,
    fontWeight: '500',
    letterSpacing: Platform.OS === 'web' ? 0.2 : 0.1,
    marginTop: Platform.OS === 'web' ? 4 : 6,
    textAlign: 'center',
    width: '100%',
    paddingHorizontal: Platform.OS === 'web' ? 0 : 8,
  },
  form: {
    marginBottom: Platform.OS === 'web' ? 12 : 20,
    gap: Platform.OS === 'web' ? 4 : 16,
    width: '100%',
  },
  forgotPasswordContainer: {
    alignSelf: 'flex-end',
    marginBottom: Platform.OS === 'web' ? 12 : 16,
    marginTop: Platform.OS === 'web' ? 0 : 4,
    ...(Platform.OS === 'web' && {
      outlineWidth: 0,
      outlineColor: 'transparent',
      outlineStyle: 'solid',
    }),
  },
  forgotPasswordText: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
  },
  errorContainer: {
    borderRadius: Platform.OS === 'web' ? 8 : 10,
    padding: Platform.OS === 'web' ? 12 : 14,
    marginBottom: Platform.OS === 'web' ? 12 : 16,
    borderLeftWidth: 4,
    borderLeftColor: palette.brightMatteRed,
    width: '100%',
  },
  errorText: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    textAlign: 'center',
  },
  signInButton: {
    marginBottom: Platform.OS === 'web' ? 24 : 20,
    height: Platform.OS === 'web' ? 52 : 56,
    borderRadius: Platform.OS === 'web' ? 16 : 14,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 6,
    width: '100%',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: Platform.OS === 'web' ? 16 : 20,
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: Platform.OS === 'web' ? 0 : 4,
  },
  footerText: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
  },
  footerLink: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    fontWeight: '600',
  },
  legalLinks: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: Platform.OS === 'web' ? 16 : 20,
    flexWrap: 'wrap',
    gap: Platform.OS === 'web' ? 0 : 4,
  },
  separatorContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  legalText: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    fontWeight: '400',
    opacity: 0.7,
  },
  webContentContainer: {
    width: '55%',
    alignSelf: 'center',
    maxWidth: 520,
    minWidth: 400,
  },
  keyboardAvoidingView: {
    flex: 1,
    width: '100%',
  },
  // Mobile-specific styles
  mobileSafeArea: {
    flex: 1,
    width: '100%',
  },
  mobileKeyboardView: {
    flex: 1,
    width: '100%',
  },
  mobileScrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: Dimensions.get('window').height,
    width: '100%',
  },
  mobileContentWrapper: {
    width: '100%',
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: Dimensions.get('window').height,
    paddingVertical: 0,
  },
  // concaveCircleBackground: {
  //   position: 'absolute',
  //   top: 0,
  //   left: 0,
  //   right: 0,
  //   bottom: 0,
  //   zIndex: 0,
  //   pointerEvents: 'none',
  // },
  // concaveCircle: {
  //   position: 'absolute',
  //   top: '50%',
  //   left: '50%',
  //   width: 400,
  //   height: 400,
  //   marginTop: -200,
  //   marginLeft: -200,
  //   borderRadius: 200,
  //   backgroundColor: 'rgba(79, 61, 111, 0.08)',
  //   transform: [{ scaleY: 0.4 }],
  //   ...(Platform.OS === 'web'
  //     ? {
  //         boxShadow: 'inset 0px 0px 100px rgba(79, 61, 111, 0.15)',
  //       }
  //     : {}),
  // },
  bubblesContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    pointerEvents: 'none',
    marginBottom: 50,
  },
  bubble: {
    position: 'absolute',
    borderRadius: 1000,
  },
  bubble1: {
    width: 100,
    height: 100,
    top: '8%',
    left: '3%',
    backgroundColor: 'rgba(79, 61, 111, 0.6)',
  },
  bubble2: {
    width: 120,
    height: 120,
    top: '20%',
    right: '3%',
    backgroundColor: 'rgba(148, 117, 225, 0.6)',
  },
  bubble3: {
    width: 80,
    height: 80,
    top: '35%',
    left: '88%',
    backgroundColor: 'rgba(236, 72, 153, 0.5)',
  },
  bubble4: {
    width: 110,
    height: 110,
    top: '75%',
    left: '5%',
    backgroundColor: 'rgba(59, 130, 246, 0.5)',
  },
  bubble5: {
    width: 95,
    height: 95,
    top: '82%',
    right: '5%',
    backgroundColor: 'rgba(34, 197, 94, 0.5)',
  },
  bubble6: {
    width: 75,
    height: 75,
    top: '12%',
    left: '50%',
    backgroundColor: 'rgba(245, 158, 11, 0.5)',
  },
  bubble7: {
    width: 85,
    height: 85,
    top: '50%',
    left: '2%',
    backgroundColor: 'rgba(168, 85, 247, 0.5)',
  },
  bubble8: {
    width: 70,
    height: 70,
    top: '60%',
    right: '2%',
    backgroundColor: 'rgba(239, 68, 68, 0.5)',
  },
  bubble9: {
    width: 90,
    height: 90,
    top: '65%',
    right: '15%',
    backgroundColor: 'rgba(16, 185, 129, 0.5)',
  },
  bubble10: {
    width: 65,
    height: 65,
    top: '45%',
    left: '2%',
    backgroundColor: 'rgba(139, 92, 246, 0.5)',
  },
  bubble11: {
    width: 80,
    height: 80,
    top: '30%',
    left: '88%',
    backgroundColor: 'rgba(251, 146, 60, 0.5)',
  },
  bubble12: {
    width: 75,
    height: 75,
    top: '70%',
    left: '80%',
    backgroundColor: 'rgba(6, 182, 212, 0.5)',
  },
  bubble13: {
    width: 70,
    height: 70,
    top: '5%',
    left: '30%',
    backgroundColor: 'rgba(220, 38, 127, 0.5)',
  },
  bubble14: {
    width: 85,
    height: 85,
    top: '55%',
    right: '5%',
    backgroundColor: 'rgba(34, 197, 94, 0.5)',
  },
  bubble15: {
    width: 65,
    height: 65,
    top: '88%',
    left: '50%',
    backgroundColor: 'rgba(99, 102, 241, 0.5)',
  },
  bubble16: {
    width: 78,
    height: 78,
    top: '15%',
    left: '65%',
    backgroundColor: 'rgba(255, 193, 7, 0.6)',
  },
  bubble17: {
    width: 72,
    height: 72,
    top: '35%',
    left: '8%',
    backgroundColor: 'rgba(233, 30, 99, 0.6)',
  },
  bubble18: {
    width: 68,
    height: 68,
    top: '60%',
    right: '25%',
    backgroundColor: 'rgba(76, 175, 80, 0.6)',
  },
  bubble19: {
    width: 82,
    height: 82,
    top: '78%',
    left: '60%',
    backgroundColor: 'rgba(156, 39, 176, 0.6)',
  },
  bubble20: {
    width: 74,
    height: 74,
    top: '25%',
    right: '70%',
    backgroundColor: 'rgba(255, 87, 34, 0.6)',
  },
});

export default LoginScreen;
